import React from "react";
import styled from "styled-components/macro";
import { NavLink, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Paper as MuiPaper,
  Typography,
  Button,
  Box,
} from "@mui/material";
import { spacing } from "@mui/system";
import {
  COLORS_SUPPORT_USER_IS_ONLINE,
  COLORS_SUPPORT_USER_STATUSES,
  SUPPORT_APP_USER_CREATE,
} from "../../constants";
import HomeIcon from "@mui/icons-material/Home";
import { ServerDataGrid } from "../../components/CustomDataGrid";
import useAuth from "../../hooks/useAuth";
import { checkPermissions } from "../../utils/checkPermissions";
import StatusChip from "../../components/StatusChip";
import { SimpleSelectCell } from "../../components/CustomCells";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const columns = [
  {
    accessorKey: "id",
    filterVariant: "text",
    header: "ID",
    operator: "equals",
  },
  {
    accessorKey: "name",
    filterVariant: "text",
    header: "Name",
    operator: "equals",
  },
  {
    accessorKey: "role",
    filterVariant: "multi-select",
    header: "Role",
    Cell: SimpleSelectCell,
    operator: "contains",
  },
  {
    accessorKey: "status",
    filterVariant: "multi-select",
    header: "Account Status",
    Cell: ({ cell }) => (
      <StatusChip
        options={COLORS_SUPPORT_USER_STATUSES}
        value={cell.getValue()}
        label={cell?.column?.columnDef?.selectOptionsLookup?.[cell.getValue()]}
        size="small"
      />
    ),
    operator: "contains",
  },
  {
    accessorKey: "isOnline",
    filterVariant: "multi-select",
    header: "Status",
    Cell: ({ cell }) => (
      <StatusChip
        options={COLORS_SUPPORT_USER_IS_ONLINE}
        value={cell.getValue()}
        label={cell?.column?.columnDef?.selectOptionsLookup?.[cell.getValue()]}
        size="small"
      />
    ),
    operator: "contains",
  },
  {
    accessorKey: "email",
    filterVariant: "text",
    header: "Email",
    operator: "equals",
  },
  {
    // TODO: Fix filtering here from foreign fields
    accessorKey: "games",
    filterVariant: "text",
    header: "Apps",
    Cell: ({ cell }) =>
      `${cell.getValue().length} ${
        cell.getValue().length === 1 ? "App" : "Apps"
      }`,
    enableColumnFilter: false,
  },
];

function SupportUsersTable() {
  const navigate = useNavigate();

  return (
    <ServerDataGrid
      columns={columns}
      apiUrl={`/api/v1/support-users`}
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => navigate(`/support-users/${row.original.id}`),
        sx: {
          cursor: "pointer",
        },
      })}
    />
  );
}

function SupportUsersPage() {
  const navigate = useNavigate();
  const { permissions } = useAuth();

  return (
    <React.Fragment>
      <Helmet title="Support Users" />
      <Typography variant="h3" gutterBottom display="inline">
        Support Users
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2} mb={6}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Typography>Support Users</Typography>
      </Breadcrumbs>
      {checkPermissions([SUPPORT_APP_USER_CREATE], permissions) && (
        <Box display="flex" justifyContent="right" pb={4}>
          <Button
            variant="contained"
            onClick={() => navigate("/support-users/new")}
          >
            Create User
          </Button>
        </Box>
      )}
      <Paper>
        <SupportUsersTable />
      </Paper>
    </React.Fragment>
  );
}

export default SupportUsersPage;
